import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable, tap } from 'rxjs';

import { environment } from '../../environments/environment';
import { sanitizeReturnUrl } from '../_helpers/return -url';
import { Globals } from '../globals';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  headers: HttpHeaders;

  _baseURL: any;

  deviceId: any;

  headersLogin: any;

  deviceInfo: any;

  logoutTimeoutId!: ReturnType<typeof setTimeout>;

  private readonly boundStorageHandler: (event: StorageEvent) => void;

  constructor(
    private httpClient: HttpClient,
    private deviceService: DeviceDetectorService,
    private router: Router,
    private globals: Globals
  ) {
    this._baseURL = environment.apiUrl;
    this.regenerateHeaders();

    this.boundStorageHandler = this.handleStorageChange.bind(this);
    window.addEventListener('storage', this.boundStorageHandler);
  }

  regenerateHeaders() {
    this.deviceId = Date.now();
    this.deviceInfo = this.deviceService.getDeviceInfo();
    const devicedetail: any = {
      platform: 'Website',
      browser: this.deviceInfo.browser,
      browserVersion: this.deviceInfo.browser_version,
      osVersion: this.deviceInfo.os_version,
      deviceId: this.deviceInfo.device + this.deviceInfo.deviceType,
      appVersion: '',
      ipAddress: '',
      macAddress: '',
    };
    this.headers = new HttpHeaders()
      .set('content-type', 'application/json')
      .set('User_Agent', JSON.stringify(devicedetail));
  }

  ngOnDestroy(): void {
    window.removeEventListener('storage', this.boundStorageHandler);
  }

  private handleStorageChange(event: StorageEvent): void {
    if (event.key !== 'partner_user') {
      return;
    }

    if (event.newValue) {
      this.globals.getUser();
      this.regenerateHeaders();
      this.handleTokenInit();
      return;
    }

    if (this.logoutTimeoutId) {
      clearTimeout(this.logoutTimeoutId);
    }
    // the session died in another tab, this tab did not ask for it: bring the user back here
    this.navigateToLogin(true);
  }

  getLogin(obj: any): Observable<any> {
    this.headersLogin = new HttpHeaders()
      .set('content-type', 'application/json')
      .set(
        'User_Agent',
        `{"platform" : "website","browser" : "","browserVersion" : "","osVersion" : "", "deviceId" : ${
          this.deviceId
        },"appVersion" : "","ipAddress" : "","macAddress" : ""}`
      );
    return this.httpClient.post(
      `${this._baseURL}api/ims/v1/partner/signin`,
      obj,
      { headers: this.headersLogin }
    );
  }

  signUp(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/ims/v1/partner/signup`,
      obj,
      { headers: this.headers }
    );
  }

  verifyOtp(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/ims/v1/partner/perform/action`,
      obj,
      { headers: this.headers }
    );
  }

  _forgotPassword(obj: any): Observable<any> {
    return this.httpClient.put(
      `${this._baseURL}api/ims/v1/partner/forgot/password`,
      obj,
      { headers: this.headers }
    );
  }

  _forgotPasswordVerification(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/ims/v1/partner/verify/forgot/password`,
      obj,
      { headers: this.headers }
    );
  }

  _resendOtp(obj: any): Observable<any> {
    const device: any = `${Date.now()}lty`;
    const header1 = new HttpHeaders()
      .set('accept', 'application/json')
      .set('data-hash', 'testww1')
      .set('device-id', device)
      .set('Content-Type', 'application/json');
    return this.httpClient.put(
      `${this._baseURL}api/communicator/v1/otp/resend`,
      obj,
      { headers: header1 }
    );
  }

  _logout(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/ims/v1/partner/signout`,
      obj,
      { headers: this.headers.append('Access-Token', obj.token) }
    );
  }

  isUserLoggedIn(): boolean {
    return !!localStorage.getItem('partner_user');
  }

  checkTokenServerSide() {
    if (this.isUserLoggedIn()) {
      const { token } = JSON.parse(localStorage.getItem('partner_user')!);
      this.httpClient
        .post(
          `${this._baseURL}api/ims/v1/partner/details/token`,
          { token },
          { headers: { 'Access-Token': token } }
        )
        .subscribe({
          next: () => {},
          error: () => {
            localStorage.removeItem('partner_user');
            localStorage.removeItem('loginTime');
            window.location.reload();
          },
        });
    }
  }

  handleTokenInit() {
    if (this.isUserLoggedIn()) {
      const expirationTime = JSON.parse(
        localStorage.getItem('partner_user')!
      ).expirationTime!;
      if (
        expirationTime &&
        !Number.isNaN(expirationTime) &&
        Number(expirationTime) > 0
      ) {
        if (Number(expirationTime) < new Date().getTime()) {
          this.logout();
        } else {
          if (this.logoutTimeoutId) {
            clearTimeout(this.logoutTimeoutId);
          }
          this.logoutTimeoutId = setTimeout(
            () => {
              this.logout();
            },
            Number(expirationTime) - new Date().getTime()
          );
        }
      }
    }
  }

  /**
   * @param preserveReturnUrl keep the current page as the post-login destination. Used when the
   * session dies on its own (expiry, failed refresh), not when the user signs out deliberately.
   */
  logout(preserveReturnUrl = false) {
    localStorage.removeItem('loginTime');
    localStorage.removeItem('partner_user');
    if (this.logoutTimeoutId) {
      clearTimeout(this.logoutTimeoutId);
    }
    this.navigateToLogin(preserveReturnUrl);
  }

  private navigateToLogin(preserveReturnUrl: boolean): void {
    const returnUrl = preserveReturnUrl
      ? sanitizeReturnUrl(this.router.url)
      : null;
    this.router.navigate(
      ['/login'],
      returnUrl ? { queryParams: { returnUrl } } : {}
    );
  }

  refreshToken(): Observable<any> {
    if (!this.isUserLoggedIn()) {
      return new Observable((observer) => {
        observer.error('User not logged in');
        observer.complete();
      });
    }

    const currentUser = this.globals.getUser();
    return this.httpClient
      .post(
        `${this._baseURL}api/ims/v1/token/regenerate/token`,
        {
          token: currentUser.token,
        },
        {
          headers: this.headers,
        }
      )
      .pipe(
        tap((result: any) => {
          if (result.status === 'SUCCESS' && result.data) {
            // Update the user data in localStorage with new token and expiration time
            const updatedUser = {
              ...currentUser,
              token: result.data.token,
              expirationTime: result.data.expiresAt,
            };
            localStorage.setItem('partner_user', JSON.stringify(updatedUser));

            // Update the current user object
            this.globals.user = updatedUser;

            // Reinitialize token handling with new expiration time
            this.handleTokenInit();
          }
        })
      );
  }

  // We use this function to compare the version in the API to the version in front
  // If it's different, it means that this version of the front is cached so we reload it
  checkVersion() {
    const headers = new HttpHeaders().set(
      'Content-Type',
      'text/plain; charset=utf-8'
    );
    this.httpClient
      .get(`${environment.apiUrl}version`, { headers, responseType: 'text' })
      .subscribe({
        next: (x) => {
          if (environment.VERSION !== x && !localStorage.getItem('reloaded')) {
            localStorage.setItem('reloaded', JSON.stringify(true));
            // Reload without cache
            const { href } = window.location;
            window.location.href = href;
          } else {
            localStorage.removeItem('reloaded');
          }
        },
      });
  }
}
